import React, { useEffect, useRef, useState } from "react";
import {
  Viewer,
  WebIFCLoaderPlugin,
  NavCubePlugin,
  TreeViewPlugin,
} from "@xeokit/xeokit-sdk";
import * as WebIFC from "web-ifc";
import ViewerToolbar from "./ViewerToolbar";

const ThreeViewer = ({
  gltfUrl,
  binUrl,
  currentDate,
  activityData,
  weatherData,
}) => {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const loaderRef = useRef(null);

  // States for UI
  const [activeMode, setActiveMode] = useState("none");
  const [isWireframe, setIsWireframe] = useState(false);
  const [isGhost, setIsGhost] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize xeokit Viewer
  useEffect(() => {
    if (!containerRef.current) return;

    // 1. Create Viewer
    const viewer = new Viewer({
      canvasId: "xeokit-canvas",
      transparent: false,
      backgroundColor: [0.1, 0.1, 0.2], // Dark blue-ish like before
    });
    viewerRef.current = viewer;

    // 2. Add Plugins
    // Navigation Cube
    new NavCubePlugin(viewer, {
      canvasId: "myNavCubeCanvas",
      visible: true,
      size: 250,
      alignment: "bottomRight",
      bottomMargin: 100,
      rightMargin: 10,
    });

    // IFC Loader
    console.log("WebIFC Import Keys:", Object.keys(WebIFC)); // DEBUG

    const ifcLoader = new WebIFCLoaderPlugin(viewer, {
      WebIFC,
      IfcAPI: WebIFC.IfcAPI, // Explicitly pass the class if xeokit looks for it directly
      wasmPath: "/wasm-xeokit/", // Point to our specific folder
      objectDefaults: {
        // Optional visual defaults
        DEFAULT: {
          colorize: [0.5, 0.5, 0.5],
        },
      },
    });
    loaderRef.current = ifcLoader;

    // Camera Control settings
    viewer.cameraControl.followPointer = true;

    // Cleanup
    return () => {
      viewer.destroy();
      viewerRef.current = null;
      loaderRef.current = null;
    };
  }, []);

  // Debug Logs for UI
  const [logs, setLogs] = useState([]);
  const addLog = (msg) => setLogs((prev) => [...prev.slice(-4), msg]); // Keep last 5

  // Load Model
  useEffect(() => {
    if (!viewerRef.current || !loaderRef.current || !gltfUrl) {
      if (!gltfUrl) addLog("Waiting for IFC URL...");
      return;
    }

    const loadModel = async () => {
      setIsLoading(true);
      addLog(`Loading: ${gltfUrl}`);

      // Clear previous models
      const viewer = viewerRef.current;
      viewer.scene.clear();

      try {
        // STEP 1: Fetch with credentials (handles 401/Auth)
        addLog("Downloading IFC with credentials...");
        const response = await fetch(gltfUrl, {
          method: "GET",
          credentials: "include", // CRITICAL: Sends cookies/auth
        });

        if (!response.ok) {
          throw new Error(
            `Download Failed: ${response.status} ${response.statusText}. Check Auth/Login.`
          );
        }

        // Verify content type
        const contentType = response.headers.get("content-type");
        if (
          contentType &&
          (contentType.includes("text/html") ||
            contentType.includes("application/json"))
        ) {
          const text = await response.text();
          throw new Error(
            `Invalid Content: Got HTML/JSON instead of IFC. Preview: ${text.substring(
              0,
              50
            )}...`
          );
        }

        // STEP 2: Create Blob URL
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        addLog(
          `Blob created. Size: ${(blob.size / 1024 / 1024).toFixed(2)} MB`
        );

        // STEP 3: Load Blob URL
        addLog("Parsing IFC...");
        const model = loaderRef.current.load({
          src: blobUrl,
          edges: true,
          excludeTypes: ["IfcSpace", "IfcOpeningElement"],
        });

        model.on("loaded", () => {
          setIsLoading(false);
          addLog("Model Loaded! Initializing Camera...");
          viewer.cameraFlight.flyTo(model);
          // URL.revokeObjectURL(blobUrl); // KEEP ALIVE for debugging
        });

        model.on("error", (e) => {
          console.error("xeokit Parse Error Details:", e);
          addLog(`Parse Error: ${e}`); // Simplify to catck string/obj
          setIsLoading(false);
          // URL.revokeObjectURL(blobUrl);
        });
      } catch (e) {
        console.error("Setup Failed:", e);
        addLog(`Error: ${e.message}`);
        setIsLoading(false);
      }
    };

    // DEBUG: Check WASM
    fetch("/wasm-xeokit/web-ifc.wasm", { method: "HEAD" })
      .then((r) =>
        r.ok ? console.log("WASM Found") : console.error("WASM Missing")
      )
      .catch((e) => console.error("WASM Check Error", e));

    loadModel();
  }, [gltfUrl]);

  // Visual Diagnostics (Wireframe / Ghost)
  // ... (keep existing)

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full bg-slate-900 overflow-hidden"
    >
      {/* Main Canvas - ID MUST MATCH "canvasId" */}
      <canvas id="xeokit-canvas" className="w-full h-full block" />

      {/* NavCube Canvas */}
      <canvas
        id="myNavCubeCanvas"
        className="absolute bottom-10 right-10 z-10 w-[200px] h-[200px]"
      />

      {/* Debug Logs Overlay - FIXED POS */}
      <div className="fixed top-20 left-10 bg-red-900/90 text-white p-4 rounded border-2 border-yellow-400 z-[9999] font-mono text-sm pointer-events-none shadow-2xl">
        <h3 className="font-bold border-b mb-1">System Status:</h3>
        <div>URL: {gltfUrl || "None"}</div>
        {logs.map((log, i) => (
          <div key={i}>{log}</div>
        ))}
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-50 text-white">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span>모델 로딩 중 (xeokit)...</span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <ViewerToolbar
        activeMode={activeMode}
        isWireframe={isWireframe}
        isGhost={isGhost}
        onModeChange={setActiveMode}
        onToggleWireframe={() => setIsWireframe((prev) => !prev)}
        onToggleGhost={() => setIsGhost((prev) => !prev)}
        onReset={() => {
          const viewer = viewerRef.current;
          if (viewer) {
            viewer.cameraFlight.flyTo(viewer.scene);
          }
        }}
      />
    </div>
  );
};

export default ThreeViewer;
